import './palette.d'

export const brandedTokens = {
    brand: {
        primary: { main: '#5DB4E4', dark: '#4F5FAC', light: '#C8C8DD' },
        secondary: { main: '#E65274', dark: '#845BA4', light: '#929DB3' },
        text: { primary: '#373D3F', secondary: '#845BA4' },
        background: { default: '#FEFEFE', paper: '#FFFFFF' },
        action: { hover: '#E7E9EE', selected: '#dcdfe5' },
    },

    typography: { fontFamily: 'inherit' },
    zIndex: { appBar: 1200, drawer: 999 },

    variables: {
        primary: '#DCECF7',
        secondary: '#8793AB',
        success: '#28a745',
        info: '#DCECF7',
        warning: '#FFC107',
        error: '#F38B8F',
        light: '#F2F9FA',
        dark: '#343A40',

        white: '#FFFFFF',
        black87: 'rgba(0, 0, 0, 0.87)',

        gray: {
            100: '#f8f9fa',
            200: '#e9ecef',
            300: '#dee2e6',
            500: '#6c757d',
            600: '#6c757d',
            700: '#495057',
            800: '#343a40',
        },

        secondaryScale: {
            90: '#929DB3',
            80: '#9FA8BB',
            70: '#AAB3C4',
            60: '#B7BECC',
            50: '#C3C9D5',
            40: '#CFD4DD',
            30: '#DBDEE6',
            20: '#E7E9EE',
            10: '#F3F4F7',
        },
    },
} as const

export type BrandedTokens = typeof brandedTokens

const { brand, variables } = brandedTokens

export const customPalette = {
    primary: {
        main: brand.primary.main, // '#5DB4E4'
        light: variables.primary, // '#DCECF7'
        dark: brand.primary.dark, // '#4F5FAC'
        mainText: '#0B5B86', // 7.3:1 on #FFFFFF — AAA
        mainTextModerate: '#1976B0', // 4.9:1 on #FFFFFF — moderate (today AA), preserves brand blue
        contrastText: variables.white, // '#FFFFFF'
    },
    secondary: {
        main: brand.secondary.main, // '#E65274'
        light: brand.secondary.light, // '#929DB3'
        dark: brand.secondary.dark, // '#845BA4'
        mainText: '#9C1F3D', // 7.6:1 on #FFFFFF — AAA
        mainTextModerate: '#BC3858', // 5.3:1 on #FFFFFF — moderate (today AA), preserves brand pink
        contrastText: variables.white,
    },
    info: {
        main: variables.info, // '#DCECF7'
        light: variables.light, // '#F2F9FA'
        dark: variables.dark, // '#343A40'
        mainText: '#0B5B86', // 7.3:1 on #FFFFFF — AAA
        mainTextModerate: '#1976B0', // 4.9:1 on #FFFFFF — moderate (today AA)
        contrastText: brand.text.primary, // '#373D3F'
    },
    warning: {
        main: variables.warning, // '#FFC107'
        light: '#FFE08A',
        dark: '#7A4900',
        mainText: '#6E4A00', // 7.9:1 on #FFFFFF — AAA (dark amber)
        mainTextModerate: '#9C6A00', // 4.6:1 on #FFFFFF — moderate (today AA), recognizable amber
        contrastText: brand.text.primary,
    },
    error: {
        main: variables.error, // '#F38B8F'
        light: '#FBD0D2',
        dark: '#8A2024',
        mainText: '#A0202A', // 7.5:1 on #FFFFFF — AAA
        mainTextModerate: '#C2363B', // 5.2:1 on #FFFFFF — moderate (today AA)
        contrastText: variables.white,
    },
    success: {
        main: variables.success, // '#28a745'
        light: '#7DD394',
        dark: '#0F4D22',
        mainText: '#116526', // 7.2:1 on #FFFFFF — AAA
        mainTextModerate: '#1B7A36', // 5.3:1 on #FFFFFF — moderate (today AA)
        contrastText: variables.white,
    },

    mainText: brand.text.primary, // '#373D3F' — 11:1 on white (AAA) — primary text
    bodyText: brand.text.primary, // '#373D3F' — alias of mainText for body-text semantics
    bodyTextMuted: '#4A5874', // 7.1:1 on #FFFFFF — AAA replacement for muted #8793AB text
    hintText: '#6C7789', // 4.52:1 on #FFFFFF — WCAG 2.1 AA minimum (4.5:1) for placeholder/hint text. Do not lighten.
    surfaceLight: variables.light, // '#F2F9FA'
    surfaceDark: variables.dark, // '#343A40'

    secondaryScale: variables.secondaryScale,
} as const
